import { Type } from 'class-transformer';
import {
  IsBoolean,
  IsDefined,
  IsEmpty,
  IsObject,
  IsOptional,
  IsString,
  Matches,
  MaxLength,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { Audience, AudienceDto } from '../audience/audience.dtos';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from '../base.dto';

// Null is rejected rather than skipped: enabled and audience are NOT NULL columns.
const isSet = (_: unknown, value: unknown) => value !== undefined;

const FEATURE_FLAG_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

export type FeatureFlagConfiguration = Record<string, unknown>;

export interface FeatureFlagDto extends BaseDto {
  key: string;
  enabled: boolean;
  description?: string | null;
  audience: Audience;
  /** Shaped by the key's feature template; null for a template that takes none. */
  configuration?: FeatureFlagConfiguration | null;
}

/** A flag as it reaches the caller, without whom else it targets. */
export type ActiveFeatureFlagDto = Pick<
  FeatureFlagDto,
  'key' | 'configuration'
>;

export class CreateFeatureFlagDto extends BaseCreateDto {
  // Shape only; the key must also name a feature template, which the service checks.
  @Matches(FEATURE_FLAG_KEY_PATTERN, { message: 'key must be camelCase' })
  @MaxLength(100)
  key: string;

  @IsBoolean()
  enabled: boolean;

  @IsOptional()
  @IsString()
  description?: string;

  @IsDefined()
  @IsObject()
  @ValidateNested()
  @Type(() => AudienceDto)
  audience: AudienceDto;

  @IsOptional()
  @IsObject()
  configuration?: FeatureFlagConfiguration;
}

export class UpdateFeatureFlagDto extends BaseUpdateDto {
  @IsEmpty({ message: 'key cannot be changed after creation' })
  key?: never;

  @ValidateIf(isSet)
  @IsBoolean()
  enabled?: boolean;

  @IsOptional()
  @IsString()
  description?: string | null;

  @ValidateIf(isSet)
  @IsObject()
  @ValidateNested()
  @Type(() => AudienceDto)
  audience?: AudienceDto;

  @IsOptional()
  @IsObject()
  configuration?: FeatureFlagConfiguration | null;
}
